import React, { useEffect, useRef, useState } from 'react'
import { effectiveWindow, staleRangeLabel } from './tracpatch.window'
import { connect } from 'react-redux'
import { Row, Button } from 'antd'
import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons'
import * as d3 from 'd3'
import moment from 'moment'
import '../../../css/patient.scss'
import { CHART_COLORS } from '../../../util/chartPalette'
import { DataViewToggle, ChartDataGrid } from '../../graph/chartDataView'
import { dayBounds, isTodayOrLater, compactHourLabel } from './tracpatch.hourly'
import { styleAxisTicks, AXIS_TITLE_FONT_SIZE, AXIS_TITLE_FONT_WEIGHT, ShowValuesSwitch } from './tracpatch.chart.style'

// Pain Score from the questionnaire. Plots the LATEST score per day (by submit
// time, however the records are ordered) over the last 7, 14 or 30 days, and
// in the 1 Day view every score submitted that day at its time of day, on a
// midnight-to-midnight axis like Steps. Score is the 0–10 Numeric Rating
// Scale (NRS) from the 'Pain Score' form; SCORE_LABELS[n] is the descriptor
// for score n. Right-justified to the last day with data so there's no blank
// tail.
//
// Each record carries `time` (the raw submit timestamp) and `date` (the same
// instant as a server-formatted MM/DD/YYYY). Days are bucketed from `time` in
// the viewer's local day, as the other charts do; `date` is the fallback for
// a record without one.

const SCORE_LABELS = [
  'No pain', // 0
  'Barely noticeable', // 1
  'Very mild', // 2
  'Mild', // 3
  'Uncomfortable', // 4
  'Distracting', // 5
  'Distressing', // 6
  'Intense', // 7
  'Severe', // 8
  'Excruciating', // 9
  'Worst possible pain', // 10
]

const TracPatchPainScoreChart = (props) => {
  const svgRef = useRef(null)
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  const [days, setDays] = useState(30)
  const [day, setDay] = useState(() => moment().startOf('day'))
  const [view, setView] = useState('chart')
  // Off by default, as on the Steps chart: a number at every point turns a
  // 30-day series into a wall of digits. See tracpatch.chart.style.js.
  const [showValues, setShowValues] = useState(false)

  // 1 Day = every submission of one local day at its time of day.
  const hourly = days === 1

  useEffect(() => {
    if (view === 'chart') draw()
  }, [props.data, days, view, day.valueOf(), showValues]) // eslint-disable-line react-hooks/exhaustive-deps

  // The moment a record was submitted: `time` when present, else its date.
  const when = (rec) => {
    const t = rec.time ? moment(rec.time) : null
    return t && t.isValid() ? t : moment(rec.date, 'MM/DD/YYYY')
  }

  var getDailyScores = () => {
    if (!props.data || props.data.length === 0) return { dailyData: [] }

    if (hourly) {
      const { start, end } = dayBounds(day)
      const dailyData = props.data
        .filter((rec) => rec && rec.score != null)
        .map((rec) => ({ at: when(rec), rec }))
        .filter(({ at }) => at.isValid() && !at.isBefore(start) && at.isBefore(end))
        .sort((a, b) => a.at.valueOf() - b.at.valueOf())
        .map(({ at, rec }) => ({ date: at.toDate(), time: rec.time || null, score: rec.score }))
      return { dailyData, windowStart: start.toDate(), windowEnd: end.toDate() }
    }

    // Window falls back to the patient's latest reading when the requested
    // range holds none — see tracpatch.window.js for why, and why widening
    // the default range is not the fix.
    // `when` is this chart's own date extractor -- it handles `time` vs a
    // `date` string in MM/DD/YYYY, which a bare moment() would misparse.
    const win = effectiveWindow(props.data, days, when)
    const defaultStart = win.windowStart
    const today = win.windowEnd

    // One score per day: the LATEST submission of that day by its time, so
    // the order the records arrive in cannot change which one is shown.
    const byDay = {}
    props.data.forEach((rec, idx) => {
      if (!rec || rec.score == null) return
      const at = when(rec)
      if (!at.isValid()) return
      if (at.isBefore(defaultStart)) return
      const key = at.format('YYYY-MM-DD')
      const cur = byDay[key]
      if (!cur || at.valueOf() > cur.at || (at.valueOf() === cur.at && idx > cur.order)) {
        byDay[key] = { score: rec.score, time: rec.time || null, at: at.valueOf(), order: idx }
      }
    })

    const dataDays = Object.keys(byDay).sort()
    if (dataDays.length === 0) return { dailyData: [] }

    const firstData = moment(dataDays[0], 'YYYY-MM-DD').startOf('day')
    const lastData = moment(dataDays[dataDays.length - 1], 'YYYY-MM-DD').startOf('day')
    const windowStart = firstData.isAfter(defaultStart) ? firstData : defaultStart
    const windowEnd = lastData.isBefore(today) ? lastData : today

    const dailyData = []
    const cursor = windowStart.clone()
    while (cursor.isSameOrBefore(windowEnd)) {
      const key = cursor.format('YYYY-MM-DD')
      const rec = byDay[key]
      dailyData.push({
        date: cursor.clone().toDate(),
        // Actual submit timestamp of the day's last reading, for the grid.
        time: rec ? rec.time : null,
        score: rec ? rec.score : null,
      })
      cursor.add(1, 'day')
    }
    return { dailyData, windowStart: windowStart.toDate(), windowEnd: windowEnd.toDate(), isStale: win.isStale }
  }

  var draw = () => {
    const { dailyData, windowStart, windowEnd } = getDailyScores()
    if (!dailyData || dailyData.length === 0) return

    const margin = { top: 20, right: 20, bottom: 60, left: 75 }
    const screenWidth = containerRef.current ? containerRef.current.offsetWidth : 400
    const screenHeight = 300
    const width = screenWidth - margin.left - margin.right
    const height = screenHeight - margin.top - margin.bottom

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    // A redraw replaces the point under the pointer, so its mouseout never
    // fires; every draw starts with the tooltip hidden (see the Steps chart).
    d3.select(tooltipRef.current).style('visibility', 'hidden')

    const g = svg
      .attr('width', screenWidth)
      .attr('height', screenHeight)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    const x = d3.scaleTime().domain([windowStart, windowEnd]).range([0, width])
    const y = d3.scaleLinear().domain([0, 11]).range([height, 0])

    // 1 Day: midnight to midnight, a tick every two hours ("12a" … "10p", "12a").
    const xAxis = hourly
      ? d3.axisBottom(x).ticks(d3.timeHour.every(2)).tickFormat((t) => compactHourLabel(moment(t).hour()))
      : d3.axisBottom(x).ticks(days > 20 ? 8 : 7).tickFormat(d3.timeFormat('%m/%d'))
    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(xAxis)
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .call(styleAxisTicks, days > 20 ? 10 : 11)
      .style('fill', 'black')
      .style('stroke', 'none')

    g.append('text')
      .attr('text-anchor', 'end')
      .attr('x', width / 2 + margin.left)
      .attr('y', height + margin.bottom - 5)
      .attr('stroke', 'none')
      .attr('fill', 'black')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text(hourly ? 'Time' : 'Date')

    g.append('g')
      .call(
        d3
          .axisLeft(y)
          .tickValues([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10])
          .tickFormat((d) => d)
      )
      .selectAll('text')
      .style('fill', 'black')
      .style('stroke', 'none')
      .call(styleAxisTicks, 10)

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 12)
      .attr('x', -height / 2)
      .attr('text-anchor', 'middle')
      .attr('fill', 'black')
      .attr('stroke', 'none')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text('Pain Level (0–10)')

    const surgeryRaw = !hourly && props.patient?.surgery?.date
    if (surgeryRaw) {
      const surgeryDate = moment(surgeryRaw).startOf('day').toDate()
      if (surgeryDate >= windowStart && surgeryDate <= windowEnd) {
        const surgeryX = x(surgeryDate)
        g.append('line')
          .attr('x1', surgeryX)
          .attr('x2', surgeryX)
          .attr('y1', 0)
          .attr('y2', height)
          .attr('stroke', '#595959')
          .attr('stroke-width', 1.5)
          .attr('stroke-dasharray', '4,3')
        const labelText = `Surgery ${moment(surgeryDate).format('MM/DD')}`
        const labelOnLeft = surgeryX > width - 80
        g.append('text')
          .attr('x', surgeryX + (labelOnLeft ? -4 : 4))
          .attr('y', 10)
          .attr('text-anchor', labelOnLeft ? 'end' : 'start')
          .attr('fill', '#595959')
          .attr('stroke', 'none')
          .style('font-size', '11px')
          .style('font-weight', '600')
          .text(labelText)
      }
    }

    const tooltip = d3.select(tooltipRef.current)
    const color = CHART_COLORS.lineBlue

    // Connect across days without a submission so the line is continuous;
    // dots still mark only days the patient actually submitted.
    const lineData = dailyData.filter((d) => d.score != null)
    const lineGen = d3
      .line()
      .x((d) => x(d.date))
      .y((d) => y(d.score))
      .curve(d3.curveMonotoneX)

    g.append('path')
      .datum(lineData)
      .attr('fill', 'none')
      .attr('stroke', color)
      .attr('stroke-width', 2)
      .attr('d', lineGen)

    g.selectAll('.dot-pain')
      .data(dailyData.filter((d) => d.score != null))
      .enter()
      .append('circle')
      .attr('class', 'dot-pain')
      .attr('cx', (d) => x(d.date))
      .attr('cy', (d) => y(d.score))
      .attr('r', 4)
      .attr('fill', color)
      .style('cursor', hourly ? 'pointer' : 'zoom-in')
      .on('mouseover', function (d) {
        const whenLabel = hourly
          ? `<b>Time</b>: ${moment(d.date).format('h:mm A')}`
          : `<b>Date</b>: ${moment(d.date).format('MM/DD')}`
        tooltip
          .style('visibility', 'visible')
          .html(
            `${whenLabel}<br/>` +
              `<b>Pain</b>: ${d.score} – ${SCORE_LABELS[d.score] || ''}` +
              (hourly ? '' : '<br/><i>Click for this day</i>')
          )
          .style('top', d3.select(this).attr('cy') - 50 + 'px')
          .style('left', parseInt(d3.select(this).attr('cx')) + margin.left + 10 + 'px')
      })
      .on('mouseout', () => tooltip.style('visibility', 'hidden'))
      // Click a day -> every score submitted that day.
      .on('click', (d) => {
        if (hourly) return
        tooltip.style('visibility', 'hidden')
        setDay(moment(d.date).startOf('day'))
        setDays(1)
      })

    // Value labels -- OFF unless the chart's "Show values" switch is on.
    if (!showValues) return
    const labelStep = days === 30 ? 2 : 1
    const labelData = dailyData.filter((d) => d.score != null)
    labelData.forEach((d, i) => {
      const fromEnd = labelData.length - 1 - i
      if (fromEnd % labelStep !== 0) return
      g.append('text')
        .attr('class', 'point-label')
        .attr('x', x(d.date))
        .attr('y', y(d.score) - 5)
        .attr('text-anchor', 'middle')
        .attr('fill', color)
        .style('font-size', '10px')
        .style('font-weight', '600')
        .style('pointer-events', 'none')
        .text(d.score)
    })
  }

  const { dailyData } = getDailyScores()
  // Recomputed for the banner from the same inputs the chart uses, so the
  // two cannot disagree about which span is on screen.
  const stale = effectiveWindow(props.data, days, when)
  // Data View: in 1 Day every submission of the day with its time; otherwise
  // the day's latest, stamped with that submission's time.
  const gridRows = dailyData
    .filter((d) => d.score != null)
    .map((d) => ({
      date: d.time || d.date,
      value: SCORE_LABELS[d.score] ? `${d.score} – ${SCORE_LABELS[d.score]}` : `${d.score}`,
    }))

  // Day navigation (1 Day only): back one day at a time as far as the first
  // submission, forward one day at a time up to today.
  let firstDay = null
  ;(props.data || []).forEach((rec) => {
    if (!rec || rec.score == null) return
    const at = when(rec)
    if (at.isValid() && (!firstDay || at.isBefore(firstDay))) firstDay = at.clone().startOf('day')
  })
  const canGoBack = !!firstDay && day.isAfter(firstDay)
  const canGoForward = !isTodayOrLater(day)
  const shiftDay = (n) => setDay(day.clone().add(n, 'day').startOf('day'))

  return (
    <>
      <center>
        <h4>Pain Score</h4>
      </center>
      {/* Says plainly that the span on screen is not the span requested.
          Shared wording via staleRangeLabel so every chart says it the same
          way -- see tracpatch.window.js. */}
      {!hourly && stale.isStale && (
        <Row justify='start' style={{ marginBottom: 8, paddingLeft: 12 }}>
          <div style={{ background:'#fff8e1', border:'1px solid var(--orange-color, #f5881f)',
                        borderRadius:8, padding:'6px 12px', fontSize:12, color:'#8a4b00', maxWidth:'100%' }}>
            {staleRangeLabel(stale.windowStart, stale.windowEnd, days)}
          </div>
        </Row>
      )}
      <Row justify='start' className='chartControls' style={{ marginBottom: 8, paddingLeft: 12 }}>
        <Button.Group size='small'>
          <Button type={days === 1 ? 'primary' : 'default'} onClick={() => setDays(1)}>1 Day</Button>
          <Button type={days === 7 ? 'primary' : 'default'} onClick={() => setDays(7)}>7 Days</Button>
          <Button type={days === 14 ? 'primary' : 'default'} onClick={() => setDays(14)}>14 Days</Button>
          <Button type={days === 30 ? 'primary' : 'default'} onClick={() => setDays(30)}>30 Days</Button>
        </Button.Group>
        <ShowValuesSwitch id='painscore-show-values' checked={showValues} onChange={setShowValues} />
        <DataViewToggle
          active={view === 'data'}
          onClick={() => setView(view === 'data' ? 'chart' : 'data')}
        />
      </Row>
      {hourly && (
        <Row justify='center' align='middle' className='chartDayNav' style={{ marginBottom: 8, gap: 8 }}>
          <Button
            size='small'
            aria-label='Previous day'
            icon={<CaretLeftOutlined />}
            disabled={!canGoBack}
            onClick={() => shiftDay(-1)}
          />
          <span style={{ minWidth: 150, textAlign: 'center', fontWeight: 600 }}>{day.format('ddd, MMM D, YYYY')}</span>
          <Button
            size='small'
            aria-label='Next day'
            icon={<CaretRightOutlined />}
            disabled={!canGoForward}
            onClick={() => shiftDay(1)}
          />
        </Row>
      )}
      {dailyData.length > 0 ? (
        view === 'data' ? (
          <ChartDataGrid rows={gridRows} valueLabel='Pain (0–10)' csvName='pain-score' />
        ) : (
          <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            <div ref={tooltipRef} className='chartToolTip' style={{ position: 'absolute', visibility: 'hidden', zIndex: 10 }} />
            <svg style={{ width: '100%', height: '300px' }} ref={svgRef} />
          </div>
        )
      ) : (
        <Row justify='center' align='middle' style={{ height: 200 }}>
          <h2>{hourly ? 'No pain score recorded for this day' : 'Data not available'}</h2>
        </Row>
      )}
    </>
  )
}

const mapStateToProps = (state) => ({ patient: state.patient.patient })
const mapDispatchToProps = () => ({})
export default connect(mapStateToProps, mapDispatchToProps)(TracPatchPainScoreChart)
